import {
  Cancel01Icon,
  BankIcon,
  File02Icon,
  WorkIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { Dispatch, SetStateAction } from "react";
import {
  IconButton,
  IconButtonSize,
  Checkbox,
  CheckboxSize
} from "shared/components";
import { CombinedIncomeConfigs, DocumentVerificationType } from "shared/types";
import { VerificationMethodRow } from "./components/VerificationMethodRow";
import { VerificationType } from "../types";
import { Card } from "shared/components/Card";

interface Props {
  onClose: () => void;
  selectedMethods: VerificationType[];
  setSelectedMethods: Dispatch<SetStateAction<VerificationType[]>>;
  selectedDocTypes: DocumentVerificationType[];
  setSelectedDocTypes: Dispatch<SetStateAction<DocumentVerificationType[]>>;
  settings?: CombinedIncomeConfigs<unknown>;
}

const DOC_TYPE_LABELS: Record<DocumentVerificationType, string> = {
  [DocumentVerificationType.Paystub]: "Pay stubs",
  [DocumentVerificationType.W2]: "W2",
  [DocumentVerificationType.BankStatement]: "Bank statement",
  [DocumentVerificationType.BenefitsStatement]: "Benefits statement"
};

export const SelectMethodsView = ({
  onClose,
  selectedMethods,
  setSelectedMethods,
  selectedDocTypes,
  setSelectedDocTypes,
  settings
}: Props) => {
  const toggleMethod = (method: VerificationType) =>
    setSelectedMethods((prev) =>
      prev.includes(method)
        ? prev.filter((m) => m !== method)
        : [...prev, method]
    );

  const toggleDocType = (docType: DocumentVerificationType) =>
    setSelectedDocTypes((prev) =>
      prev.includes(docType)
        ? prev.filter((d) => d !== docType)
        : [...prev, docType]
    );

  const enabledDocTypes = [
    settings?.[DocumentVerificationType.Paystub].allowed &&
      DocumentVerificationType.Paystub,
    settings?.[DocumentVerificationType.W2].allowed &&
      DocumentVerificationType.W2,
    settings?.[DocumentVerificationType.BankStatement].allowed &&
      DocumentVerificationType.BankStatement
  ].filter((type): type is DocumentVerificationType => Boolean(type));

  const hasDocumentsMethod = enabledDocTypes.length;
  const hasBankMethod = settings?.bank_allowed;
  const hasPayrollMethod = settings?.payroll_allowed;

  return (
    <div className="flex flex-col h-full">
      <header className="p-6 border-b border-alpha-100">
        <h1 className="text-xl text-content-1000 font-bold mb-1 relative">
          Select verification method
          <IconButton
            HugeIcon={Cancel01Icon}
            size={IconButtonSize.SM}
            borderLess
            className="absolute -top-2 -right-2"
            onClick={onClose}
          />
        </h1>
        <p className="text-sm font-medium text-content-400">
          Applicant will be able to choose from the following verification
          methods
        </p>
      </header>

      <div className="flex-grow bg-background-900 p-6 space-y-3 overflow-y-auto">
        <Card className="p-5 space-y-5">
          {hasBankMethod && (
            <VerificationMethodRow
              icon={BankIcon}
              label="Bank account"
              checked={selectedMethods.includes(VerificationType.Bank)}
              onChange={() => toggleMethod(VerificationType.Bank)}
            />
          )}

          {hasPayrollMethod && (
            <VerificationMethodRow
              icon={WorkIcon}
              label="Payroll provider"
              checked={selectedMethods.includes(VerificationType.Payroll)}
              onChange={() => toggleMethod(VerificationType.Payroll)}
            />
          )}

          {hasDocumentsMethod && (
            <>
              <VerificationMethodRow
                icon={File02Icon}
                label="Documents"
                checked={selectedMethods.includes(VerificationType.Documents)}
                onChange={() => toggleMethod(VerificationType.Documents)}
              />

              {selectedMethods.includes(VerificationType.Documents) && (
                <div className="pl-[60px] space-y-3">
                  <hr className="border-alpha-100" />
                  <p className="text-sm font-medium text-content-400 pt-1">
                    Select document type
                  </p>
                  <div className="space-y-2 w-fit">
                    {enabledDocTypes.map((dt) => (
                      <Checkbox
                        key={dt}
                        id={`doc_type_${dt}`}
                        name={`doc_type_${dt}`}
                        label={DOC_TYPE_LABELS[dt]}
                        checked={selectedDocTypes.includes(dt)}
                        onChange={() => toggleDocType(dt)}
                        size={CheckboxSize.SM}
                        wrapperClassName="block cursor-pointer"
                        className="cursor-pointer"
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
